import React, { useMemo } from "react";
import { ChevronRight, RotateCcw, AlertTriangle } from "lucide-react";

/**
 * Warning Light Fault Tree — DEEP branching diagnostic intake.
 *
 * Captures structured symptom intelligence the same way a senior advisor
 * would in a workshop intake form — every question conditional on previous
 * answers, so the FOH only sees what's relevant.
 *
 * 4–5 levels of structured triage:
 *   1. Lamp colour                  (red / amber / yellow / unknown)
 *   2. Which lamp (filtered by colour)
 *   3. Behaviour (steady / flashing / on intermittently / on at start, off after)
 *   4. Vehicle driveability + accompanying symptoms
 *   5. Recent triggers (fuel fill / wash / recent work / cold weather / none)
 *
 * State shape (passed up via onChange):
 *   {
 *     lamp_colour, lamp_identity, lamp_behaviour, driveable,
 *     accompanying_symptoms: [], recent_trigger, time_since_first_seen,
 *     miles_since_first_seen, notes
 *   }
 *
 * Props
 *   data       — current capture (controlled)
 *   onChange   — (patch) => void
 *   onReset    — () => void (optional)
 */
export default function WarningLightFaultTree({
  data = {},
  onChange = () => {},
  onReset,
}) {
  const set = (key) => (value) => onChange({ [key]: value });

  // ----- branch selectors ---------------------------------------------------
  const lampOptions = useMemo(
    () => LAMP_BY_COLOUR[data.lamp_colour] || [],
    [data.lamp_colour]
  );

  const showLampIdentity = !!data.lamp_colour;
  const showBehaviour = !!data.lamp_identity;
  const showDriveable = !!data.lamp_behaviour;
  const showSymptoms = !!data.driveable;
  // Only ask for triggers / age once the FOH has captured driveability
  const showTriggers = !!data.driveable;
  const showAge = !!data.driveable;

  // Critical-lamp banner — surfaces only on RED + steady/flashing
  const isCritical =
    data.lamp_colour === "red" &&
    (data.lamp_behaviour === "steady" || data.lamp_behaviour === "flashing");

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="warning-light-fault-tree"
    >
      <header className="px-4 py-3 bg-gradient-to-r from-rose-50 via-amber-50 to-white border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-rose-700 font-semibold">
            Diagnostic · Fault tree
          </p>
          <h4 className="text-[16px] font-bold text-slate-900 leading-tight">
            Warning light triage
          </h4>
        </div>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800"
            data-testid="warning-light-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        )}
      </header>

      <div className="p-4 space-y-3.5">
        {/* Level 1 — Lamp colour */}
        <Branch
          step={1}
          label="Lamp colour"
          subtitle="Red usually means stop / serious. Amber means investigate."
          testId="branch-lamp-colour"
        >
          <Chips
            value={data.lamp_colour}
            options={[
              { v: "red", label: "Red" },
              { v: "amber", label: "Amber" },
              { v: "yellow", label: "Yellow" },
              { v: "unknown", label: "Unsure" },
            ]}
            onChange={(v) => {
              // Resetting colour clears all downstream answers
              onChange({
                lamp_colour: v,
                lamp_identity: null,
                lamp_behaviour: null,
                driveable: null,
                accompanying_symptoms: [],
                recent_trigger: null,
              });
            }}
            testId="lamp-colour"
          />
        </Branch>

        {/* Critical lamp banner */}
        {isCritical && (
          <div
            className="rounded-md border border-rose-300 bg-rose-50 px-3.5 py-2.5 flex items-start gap-2.5"
            data-testid="critical-lamp-banner"
          >
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-[13.5px] text-rose-900 leading-snug">
              <p className="font-semibold">Critical lamp behaviour.</p>
              <p>
                Advise customer to stop driving and arrange recovery —
                continuing to drive may cause further damage.
              </p>
            </div>
          </div>
        )}

        {/* Level 2 — Which lamp (filtered by colour) */}
        {showLampIdentity && (
          <Branch
            step={2}
            label="Which warning light?"
            subtitle={
              data.lamp_colour === "unknown"
                ? "Pick the closest match from the full list."
                : `Common ${data.lamp_colour} lamps shown first.`
            }
            testId="branch-lamp-identity"
          >
            <Chips
              value={data.lamp_identity}
              options={lampOptions.map((o) => ({ v: o, label: o }))}
              onChange={(v) =>
                onChange({
                  lamp_identity: v,
                  lamp_behaviour: null,
                  driveable: null,
                })
              }
              testId="lamp-identity"
              wrap
            />
          </Branch>
        )}

        {/* Level 3 — Behaviour */}
        {showBehaviour && (
          <Branch
            step={3}
            label="Lamp behaviour"
            subtitle="Flashing is more urgent than steady. Catch this carefully."
            testId="branch-lamp-behaviour"
          >
            <Chips
              value={data.lamp_behaviour}
              options={[
                { v: "steady", label: "Steady on" },
                { v: "flashing", label: "Flashing" },
                { v: "intermittent", label: "Comes on intermittently" },
                { v: "start_then_off", label: "On at start, goes off" },
              ]}
              onChange={(v) =>
                onChange({
                  lamp_behaviour: v,
                  driveable: null,
                })
              }
              testId="lamp-behaviour"
            />
          </Branch>
        )}

        {/* Level 4 — Driveability */}
        {showDriveable && (
          <Branch
            step={4}
            label="Is the vehicle still being driven?"
            subtitle="If unsafe, advise customer to stop driving."
            testId="branch-driveable"
          >
            <Chips
              value={data.driveable}
              options={[
                { v: "yes_normal", label: "Yes — feels normal" },
                { v: "yes_degraded", label: "Yes — but degraded" },
                { v: "limp_mode", label: "Limp / restricted mode" },
                { v: "no_unsafe", label: "No — unsafe to drive" },
              ]}
              onChange={set("driveable")}
              testId="driveable"
            />
          </Branch>
        )}

        {/* Level 5a — Accompanying symptoms */}
        {showSymptoms && (
          <Branch
            step={5}
            label="Accompanying symptoms"
            subtitle="Tick any the customer mentions — these speed up diagnosis hugely."
            testId="branch-symptoms"
          >
            <Chips
              multi
              value={data.accompanying_symptoms || []}
              options={SYMPTOM_OPTIONS.map((o) => ({ v: o, label: o }))}
              onChange={(arr) => onChange({ accompanying_symptoms: arr })}
              testId="accompanying-symptoms"
              wrap
            />
          </Branch>
        )}

        {/* Level 5b — Recent triggers */}
        {showTriggers && (
          <Branch
            step={6}
            label="Anything happen just before?"
            subtitle="Recent triggers narrow the fault scope dramatically."
            testId="branch-triggers"
          >
            <Chips
              value={data.recent_trigger}
              options={[
                { v: "fuel_fill", label: "After a fuel fill" },
                { v: "car_wash", label: "After a car wash" },
                { v: "cold_start", label: "Cold morning / overnight" },
                { v: "recent_repair", label: "After recent repair / work" },
                { v: "hot_weather", label: "Hot weather / long drive" },
                { v: "none", label: "Nothing in particular" },
              ]}
              onChange={set("recent_trigger")}
              testId="recent-trigger"
              wrap
            />
          </Branch>
        )}

        {/* Level 5c — Age of fault */}
        {showAge && (
          <Branch
            step={7}
            label="When did the light first come on?"
            subtitle="Time + miles since first seen helps triage urgency."
            testId="branch-age"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[12.5px] font-semibold text-slate-700 mb-1">
                  Time since first seen
                </span>
                <select
                  value={data.time_since_first_seen || ""}
                  onChange={(e) => onChange({ time_since_first_seen: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-rose-500"
                  data-testid="time-since-first-seen"
                >
                  <option value="">Select…</option>
                  <option>Today</option>
                  <option>Yesterday</option>
                  <option>This week</option>
                  <option>This month</option>
                  <option>1–3 months ago</option>
                  <option>Longer / not sure</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-[12.5px] font-semibold text-slate-700 mb-1">
                  Approx. miles since first seen
                </span>
                <input
                  type="text"
                  placeholder="e.g. 150 miles"
                  value={data.miles_since_first_seen || ""}
                  onChange={(e) =>
                    onChange({ miles_since_first_seen: e.target.value })
                  }
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-rose-500"
                  data-testid="miles-since-first-seen"
                />
              </label>
            </div>
          </Branch>
        )}

        {/* Free-text notes — always available once we have a colour */}
        {data.lamp_colour && (
          <Branch
            step={8}
            label="Anything else worth noting?"
            subtitle="Customer's own words — exact quotes help the technician."
            testId="branch-notes"
          >
            <textarea
              rows={2}
              value={data.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="e.g. ‘Goes off when I rev it, comes back at idle’…"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-rose-500"
              data-testid="warning-light-notes"
            />
          </Branch>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Branch({ step, label, subtitle, testId, children }) {
  return (
    <section className="space-y-1.5" data-testid={testId}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold">
          {step}
        </span>
        <div className="min-w-0">
          <p className="text-[14.5px] font-semibold text-slate-900 leading-snug">
            {label}
          </p>
          {subtitle && (
            <p className="text-[12.5px] text-slate-500 leading-snug">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="pl-7">{children}</div>
    </section>
  );
}

function Chips({ value, options, onChange, testId, multi = false, wrap = false }) {
  const selected = multi ? new Set(value || []) : value;

  return (
    <div className={`flex gap-1.5 ${wrap ? "flex-wrap" : "flex-wrap"}`}>
      {options.map((opt) => {
        const isOn = multi ? selected.has(opt.v) : selected === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => {
              if (multi) {
                const next = new Set(selected);
                if (next.has(opt.v)) next.delete(opt.v);
                else next.add(opt.v);
                onChange(Array.from(next));
              } else {
                onChange(opt.v);
              }
            }}
            className={`px-3 py-1.5 rounded-full border text-[13.5px] font-medium transition-colors ${
              isOn
                ? "border-rose-500 bg-rose-50 text-rose-800 shadow-sm"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
            data-testid={`${testId}-${opt.v}`}
          >
            {opt.label}
            {!multi && isOn && (
              <ChevronRight className="inline w-3 h-3 ml-0.5 opacity-60" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lamp dictionary — keep tightly curated; covers the 95% real-world set.
// ---------------------------------------------------------------------------

const LAMP_BY_COLOUR = {
  red: [
    "Oil pressure",
    "Coolant temperature (overheat)",
    "Brake system",
    "Battery / charging",
    "Airbag (SRS)",
    "Power steering",
    "Stop / Engine fault (red)",
    "Other (red)",
  ],
  amber: [
    "Engine management (EML / MIL)",
    "DPF / particulate filter",
    "AdBlue / Emissions",
    "EPC / electronic power control",
    "Glow plug (diesel)",
    "ABS",
    "ESP / traction control",
    "Tyre pressure (TPMS)",
    "EML (engine warning)",
    "Other (amber)",
  ],
  yellow: [
    "Service / oil reminder",
    "Low fuel",
    "Bulb out / lamp fault",
    "Washer fluid low",
    "Other (yellow)",
  ],
  unknown: [
    "Engine management (EML / MIL)",
    "Oil pressure",
    "Coolant temperature",
    "Brake system",
    "ABS",
    "Airbag (SRS)",
    "Battery / charging",
    "DPF / particulate filter",
    "Tyre pressure (TPMS)",
    "ESP / traction control",
    "Service reminder",
    "Other / unsure",
  ],
};

const SYMPTOM_OPTIONS = [
  "Loss of power",
  "Rough idle",
  "Misfire / stutter",
  "Smoke from exhaust",
  "Unusual smell",
  "Higher fuel use",
  "Slow to start",
  "Won't start",
  "Vibration",
  "Hot engine / steam",
  "None obvious",
];
